import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { ProviderHubBasicFilters } from "@app/projects/network-assessment/provider-hub/ProvidersHubBasicFilters";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProviderHubGenerateReportFlyout from "@app/projects/network-assessment/provider-hub/ProviderHubGenerateReportFlyout";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { formatCountiesImpacted, formatSpecialtyWithCode, sortPredictedSpecialtyNames, SortByOptionToDisplay } from "@app/projects/network-assessment/provider-hub/utility";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import { getClaimsVolumeDefinitions, getEfficiencyAndEffectivenessDefinitions, getMotiveScoreDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { NPF } from "@app/projects/servicing-provider/types";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { EMPTY_DISPLAY } from "@app/projects/utility";
import { NetworkIntegrityNotificationFlag } from "@app/projects/network-assessment/provider-hub/network-integrity-components/NetworkIntegrityNotificationFlag";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderHubSortColumn, ProviderHubServicingProviders, ProviderHubServicingProvidersTableData } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

const combinedGapImpactSortOptions: ProviderHubSortColumn[] = [
  ProviderHubSortColumn.GapsClosedDescending,
  ProviderHubSortColumn.GapsClosedAscending,
];

const breakdownGapImpactSortOptions: ProviderHubSortColumn[] = [
  ProviderHubSortColumn.ActiveGapsClosedDescending,
  ProviderHubSortColumn.ActiveGapsClosedAscending,
  ProviderHubSortColumn.PendingGapsClosedDescending,
  ProviderHubSortColumn.PendingGapsClosedAscending,
  ProviderHubSortColumn.ApprovedGapsClosedDescending,
  ProviderHubSortColumn.ApprovedGapsClosedAscending,
  ProviderHubSortColumn.OpportunityGapsClosedDescending,
  ProviderHubSortColumn.OpportunityGapsClosedAscending,
];

const nameAndAccessPointsSortOptions: ProviderHubSortColumn[] = [
  ProviderHubSortColumn.NameAscending,
  ProviderHubSortColumn.NameDescending,
  ProviderHubSortColumn.AccessPointCountDescending,
  ProviderHubSortColumn.AccessPointCountAscending,
];

const pciOnlySortOptions: ProviderHubSortColumn[] = [
  ProviderHubSortColumn.EffectivenessDescending,
  ProviderHubSortColumn.EffectivenessAscending,
  ProviderHubSortColumn.EfficiencyDescending,
  ProviderHubSortColumn.EfficiencyAscending
];

// Formats a gap-impact count for display: '---' when absent/zero, otherwise "Closes N gap(s)".
// Shared by the combined Gap Impact column and the four region-status breakdown columns.
const formatGapImpact = (value?: number): string =>
  !value ? '---' : `Closes ${value} gap${value === 1 ? '' : 's'}`;

const ProviderHubProvidersView: FC = () => {
  const history = useHistory();
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const providerHubService = useRef(new ProviderHubService()).current;

  const [pendingSortSearch, setPendingSortSearch] = useState(false);

  const showPciData = !!projectContext?.runConfiguration?.showPciData;
  const showVaClaims = !!projectContext?.runConfiguration?.showVaClaimsVolumeColumn;
  const showTricareClaims = !!projectContext?.runConfiguration?.showTricareClaimsVolumeColumn;
  const showCareJourneyScores = !!projectContext?.runConfiguration?.showCareJourneyScores;
  const showMotiveScores = !!projectContext?.runConfiguration?.showMotiveAppropriatenessScore;

  // Show the four-column region-status breakdown when the backend reports that this run carries region-status
  // data; otherwise we still show the Total Gap Impact for projects that have not been rerun with the
  // region-status breakdown data yet. This indicator is result-set-level metadata (it covers every page), so
  // paging never changes which columns or Gap Impact sort options are available.
  const useRegionStatusBreakdown = providerHubContext.providerHubServicingProviders?.useGapsByRegionStatus ?? false;

  const gapImpactSortOptions = useMemo<ProviderHubSortColumn[]>(
    () => useRegionStatusBreakdown ? breakdownGapImpactSortOptions : combinedGapImpactSortOptions,
    [useRegionStatusBreakdown]
  );

  const otherSortOptions = useMemo<ProviderHubSortColumn[]>(() => {
    let options = nameAndAccessPointsSortOptions;
    if (showPciData) {
      options = options.concat(pciOnlySortOptions);
    }
    return options;
  }, [showPciData]);

  const sortByOptions = useMemo<ProviderHubSortColumn[]>(
    () => [...gapImpactSortOptions, ...otherSortOptions],
    [gapImpactSortOptions, otherSortOptions]
  );

  /* oxlint-disable sort-keys */
  const providerHubServicingProvidersTableColumnConfig: ColumnConfig = {
    name: {
      displayText: "Name",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_providers-table-provider-name",
      noSort: true
    },
    identifier: {
      displayText: "Identifier",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_providers-table-identifier",
      noSort: true
    },
    providerType: {
      displayText: "Provider Source",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      noSort: true
    },
    providerActionStatus: {
      displayText: "Provider Action Status",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-status",
      tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a provider",
      noSort: true,
      hidden: providerHubContext.isScenarioModelingProcessing || !featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION)
    },
    gapsClosed: {
      displayText: "Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the provider closes",
      noSort: true,
      hidden: useRegionStatusBreakdown
    },
    activeGapImpact: {
      displayText: "Active Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-active-gap-impact",
      tooltip: "Count of gaps closeable in Active service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    pendingGapImpact: {
      displayText: "Pending Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-pending-gap-impact",
      tooltip: "Count of gaps closeable in Pending service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    approvedGapImpact: {
      displayText: "Approved Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-approved-gap-impact",
      tooltip: "Count of gaps closeable in Approved service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    opportunityGapImpact: {
      displayText: "Opportunity Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-opportunity-gap-impact",
      tooltip: "Count of gaps closeable in Opportunity-to-expand counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    specialtiesImpacted: {
      displayText: "Specialty Groups Impacted",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-specialties-impacted",
      noSort: true
    },
    countiesImpacted: {
      displayText: "Counties Impacted",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-counties-impacted",
      noSort: true
    },
    accessPointCount: {
      displayText: "Access Points",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-access-point-count",
      tooltip:  (
      <div style={{ textAlign: "left" }}>
        An Access Point represents a unique combination of a provider&#39;s NPI, the physical service location (address and geographic coordinates), and the specialty practiced at that location.
      </div>
      ),
      noSort: true
    },
    effectivnessRank: {
      displayText: "Effectiveness (Quality)",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-effectiveness-rank",
      tooltip:  getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank),
      noSort: true,
      hidden: !showPciData
    },
    efficiencyRank: {
      displayText: "Efficiency (Cost)",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-efficiency-rank",
      tooltip:  getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank),
      noSort: true,
      hidden: !showPciData
    },
    commercialClaimsVolume: {
      displayText: "Commercial Claims",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-commercial-claims-volume",
      tooltip:  getClaimsVolumeDefinitions(),
      noSort: true,
      hidden: !showPciData
    },
    medicaidClaimsVolume: {
      displayText: "Medicaid Claims",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-medicaid-claims-volume",
      tooltip:  getClaimsVolumeDefinitions(),
      noSort: true,
      hidden: !showPciData
    },
    medicareClaimsVolume: {
      displayText: "Medicare Claims",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-medicare-claims-volume",
      tooltip:  getClaimsVolumeDefinitions(),
      noSort: true,
      hidden: !showPciData
    },
    vaClaimsVolume: {
      displayText: "VA Claims",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-va-claims-volume",
      tooltip:  getClaimsVolumeDefinitions(),
      noSort: true,
      hidden: !showVaClaims
    },
    tricareClaimsVolume: {
      displayText: "TRICARE Claims",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-tricare-claims-volume",
      tooltip:  getClaimsVolumeDefinitions(),
      noSort: true,
      hidden: !showTricareClaims
    },
    //These last columns use the 'displayElement' prop instead of the 'tooltip' one due to some custom formatting that needs to be applied, to accommodate some images and add custom widths.
    totalClaimsVolume: {
      displayElement: (
        <div style={{ alignItems: "center", display: "flex"}}>
          <TooltipWrapper text={getClaimsVolumeDefinitions()} position="bottom" tooltipWidth={showCareJourneyScores ? null : "250px"}>
            <div className="waffles-tooltip-indicator">
              <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" />
            </div>
          </TooltipWrapper>
          {"Total Claims"}
        </div>
      ),
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-total-claims-volume",
      noSort: true,
      hidden: !showPciData
    },
    careJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-carejourney-cost",
      tooltip: null,
      noSort: true,
      hidden: !showCareJourneyScores
    },
    careJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-carejourney-quality",
      tooltip: null,
      noSort: true,
      hidden: !showCareJourneyScores
    },
    motiveScores: {
      displayElement: (
        <div style={{ alignItems: "center", display: "flex"}}>
          <TooltipWrapper text={getMotiveScoreDefinitions()} position="bottom">
            <div className="waffles-tooltip-indicator">
              <QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" />
            </div>
          </TooltipWrapper>
          {"Motive Appropriateness Tiers"}
          <img src={`${window.customUIBaseUrl}/images/motive-h-color.svg`} alt="Motive Appropriateness Tiers" width="120" height="30" />
        </div>
      ),
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-motive-scores",
      tooltip: null,
      noSort: true,
      hidden: !showMotiveScores
    },
    specialtyPrediction: {
      displayElement: (
        <div style={{ alignItems: "center", display: "flex" }}>
          {"Specialty Prediction"}
        </div>
      ),
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-specialty-prediction",
      tooltip: null,
      noSort: true,
      hidden: !featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL)
    }
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => handlePageChange(pageNumber),
    enableBackendPagination: true,
    handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
    initialPage: providerHubContext.pageIndex,
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: providerHubContext.pageSize,
    resetToFirstPage: providerHubContext.resetToFirstPage,
    showPageSizeSelector: true,
    totalRecords: providerHubContext.providerHubServicingProviders.totalCount
  }
  /* oxlint-enable sort-keys */

  const handlePageSizeChange = (pageSize: number) => {
    providerHubContext.setPageSize(pageSize);
    providerHubContext.setPageIndex(1);
  }

  const handlePageChange = async (newPage: number) => {
    if (!providerHubContext.resetToFirstPage) {
      providerHubContext.setPageIndex(newPage);
    }
    if (providerHubContext.resetToFirstPage) {
      providerHubContext.setResetToFirstPage(false);
    }
  };

  const handleProviderClick = async (provider: ProviderHubServicingProviders) => {
    const selectedProviderDetails = await providerHubService.getServicingProviderDetails(projectContext?.project?.projectId, provider.projectRunConfigurationId, provider.servicingProviderKey);
    providerHubContext.navigateToProviderProfile(selectedProviderDetails, ProviderProfileReferrerEnum.ProviderHubLandingPage);
  }

  const handleSortChange = (newSortValue: ProviderHubSortColumn) => {
    providerHubContext.setFilter('sortColumnAndOrder', newSortValue)
    setPendingSortSearch(true);
  };

  useEffect(() => {
    if (pendingSortSearch) {
      providerHubContext.handleSearchClick();
    }
    setPendingSortSearch(false);
  }, [providerHubContext.filterState.sortColumnAndOrder, pendingSortSearch]);

  // When the display mode flips (combined <-> breakdown) the previously selected sort may no
  // longer appear in the dropdown; reset to the default and re-search (via the same
  // pendingSortSearch mechanism handleSortChange uses) so the results actually reflect the
  // reset sort, not just the dropdown label.
  useEffect(() => {
    const currentSort = providerHubContext.filterState.sortColumnAndOrder;
    if (!sortByOptions.includes(currentSort)) {
      providerHubContext.setFilter('sortColumnAndOrder', ProviderHubSortColumn.NameAscending);
      setPendingSortSearch(true);
    }
  }, [sortByOptions]);

  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);

  /* oxlint-disable sort-keys */
  const providersTableData: ProviderHubServicingProvidersTableData[] = useMemo<ProviderHubServicingProvidersTableData[]>(() => {

    let data: ProviderHubServicingProvidersTableData[] = [];

    data = providerHubContext.providerHubServicingProviders.items.map((provider) => {
      return {
        key: provider.servicingProviderKey,
        name: <div className="provider-hub-providers-view__provider-name-content">
          <QESButton
            qesType="link"
            qesTestId={`provider-hub-providers-view__provider--${provider.npi}`}
            onClick={() => { handleProviderClick(provider) }}
            className="demibold provider-hub-providers-view__provider-name-button"
          >
            <span className="provider-hub-providers-view__provider-name">{provider.name ?? "---"}</span>
          </QESButton>
          <NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={provider.hasNetworkIntegrityFailure} />
        </div>,
        identifier: (
          <>
            {provider.npi
              ? (
                <div className="provider-hub-providers-view__identifier-row">
                  <span className="provider-hub-providers-view__identifier-label">NPI:</span> {provider.npi}
                </div>
              )
              : "---"}
            {provider.tins?.length > 0 && (
              <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
                <div className="provider-hub-providers-view__tins">
                  <span className="provider-hub-providers-view__identifier-label">TIN:</span>
                  <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={provider.tins}
                    numberOfElementsPerRow={1}
                    separator=", " />
                </div>
              </RoleRestricted>
            )}
          </>
        ),
        providerType: (
          <>
            {sortProviderNetworkTypes(provider.providerNetworkTypes).map((networkType) =>
              <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
            )}
          </>
        ),
        providerActionStatus: provider.providerActionStatuses.length > 0 ? (
          <>
            {provider.providerActionStatuses.map((status, index) => (
              <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} status={status} key={index} />
            )
            )}
          </>
        ) : (
          <>No Actions Applied</>
        ),
        gapsClosed: formatGapImpact(provider.gapsClosed),
        activeGapImpact: formatGapImpact(provider.activeGapsClosed),
        pendingGapImpact: formatGapImpact(provider.pendingGapsClosed),
        approvedGapImpact: formatGapImpact(provider.approvedGapsClosed),
        opportunityGapImpact: formatGapImpact(provider.opportunityGapsClosed),
        specialtiesImpacted: <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={provider.specialtiesImpacted?.map(formatSpecialtyWithCode)}
          numberOfElementsPerRow={3} />,
        countiesImpacted: <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={formatCountiesImpacted(provider.countiesImpacted)}
          numberOfElementsPerRow={3} />,
        accessPointCount: provider.accessPointCount,
        effectivnessRank: provider.effectivenessRank?.toString() ?? "---",
        efficiencyRank: provider.efficiencyRank?.toString() ?? "---",
        commercialClaimsVolume: provider.commercialClaimsVolume ?? "---",
        medicaidClaimsVolume: provider.medicaidClaimsVolume ?? "---",
        medicareClaimsVolume: provider.medicareClaimsVolume ?? "---",
        vaClaimsVolume: provider.vaClaimsVolume ?? "---",
        tricareClaimsVolume: provider.tricareClaimsVolume ?? "---",
        totalClaimsVolume: provider.totalClaimsVolume ?? "---",
        motiveScores: !provider.motiveScores || provider.motiveScores.length === 0 ? (
          <div>{EMPTY_DISPLAY}</div>
        ): (
          <>
            {provider.motiveScores.map((score, index) => (
              <div key={`${score.domainDescription}-${score.starValue}-${index}`}>
                {score.starValue} ({score.domainDescription})
              </div>
            ))}
          </>
        ),
        careJourneyCost: provider.careJourneyScoresCostScore?.toString() ?? "---",
        careJourneyQuality: provider.careJourneyScoresQualityScore?.toString() ?? "---",
        specialtyPrediction: <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={sortPredictedSpecialtyNames(provider.predictedSpecialtyNames)}
          numberOfElementsPerRow={3}
          separator=", " />,
      }
    });

    return data;
  }, [providerHubContext.providerHubServicingProviders]);
  /* oxlint-enable sort-keys */

  return (
    <PageSection>
      <>
        <ProviderHubBasicFilters />

        {/* Used to go back to PCI Dashboard after drill-in */}
        {providerHubContext.enableProvidersViewBackButton && (
          <div className="provider-hub-providers-view__back-button-container">
            <QESButton
              className="mt-1"
              onClick={() => history.goBack()}
              qesType="outline-inherit"
              qesTestId="provider-hub-providers-view__back-btn"
            >
              Go Back
            </QESButton>
          </div>
        )}

        <div className="provider-hub-providers-table-info-header">
          <div>
            <h5>Providers</h5>
            <p>View, select and action on providers listed below</p>
            <QESSelect
              allowEmpty={false}
              containerClassName="sort-by-option"
              testId="provider-hub__sort-by-select"
              label="Sort by"
              onChange={(e) => handleSortChange(e.target.value as ProviderHubSortColumn)}
              value={providerHubContext.filterState.sortColumnAndOrder}
            >
              <optgroup label="Gap Impact">
                {gapImpactSortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SortByOptionToDisplay(option)}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Other">
                {otherSortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SortByOptionToDisplay(option)}
                  </option>
                ))}
              </optgroup>
            </QESSelect>
          </div>
          {providersTableData.length > 0 && (
            <QESButton
              qesType="primary"
              qesTestId="provider-hub-providers-view__generate-report"
              onClick={() => setIsGenerateReportModalOpen(true)}
            >
              Generate Report
            </QESButton>
          )}
        </div>
        <ProviderHubGenerateReportFlyout
          isModalOpen={isGenerateReportModalOpen}
          setIsModalOpen={setIsGenerateReportModalOpen}
          filters={providerHubContext.filterState}
        />
        <div className="provider-hub-providers-view__record-count">
          <p>Providers: {providerHubContext.providerHubServicingProviders?.totalCount?.toLocaleString() ?? 0}</p>
        </div>
        <Waffles<ProviderHubServicingProvidersTableData>
          columns={providerHubServicingProvidersTableColumnConfig}
          data={providersTableData}
          totalNumberOfRecords={providersTableData.length}
          enablePagination={true}
          emptyTableMessage="No matching provider results found. Please start a new search"
          fullWidth={true}
          loading={providerHubContext.loading}
          paginationProps={paginationProps}
          testId="provider-hub__providers-table"
          variant="frozen-border"
        />
      </>
    </PageSection>
  )
}

export default ProviderHubProvidersView;